/**
 * decideConfig — keeping a Decide node's route table in step with its
 * questions as they are edited, and reading a decide result.
 *
 * Questions are edited as rows (shared/decision/questions) with stable uids,
 * so a rename is seen as a rename: the routes' conditions follow the new
 * question name and the new option name instead of silently pointing at
 * something that no longer exists. A deleted question's conditions go; an
 * outcome a question can no longer produce is dropped from its condition.
 * Route ids never change here: they are the node's ports, and the edges
 * leaving by them stay put.
 */
import { outcomesOf, outcomeLabel, pickRoute, isRange, topOf } from 'shared/decision/routing';
import { toApi } from 'shared/decision/questions';

/** old question key → new key, for the rows that kept their uid. */
function keyMoves(prevRows, nextRows) {
  const byUid = new Map((nextRows || []).map((r) => [r.uid, r]));
  const out = {};
  for (const p of prevRows || []) {
    const n = byUid.get(p.uid);
    if (n) out[p.key] = { key: n.key, prev: p, next: n };
  }
  return out;
}

/** old option key → new option key, for a choice question whose options kept their uid. */
function optionMoves(prev, next) {
  if (!prev || !next || prev.type !== 'choice' || next.type !== 'choice') return {};
  const byUid = new Map((next.options || []).map((o) => [o.uid, o.key]));
  const out = {};
  for (const o of prev.options || []) if (byUid.has(o.uid)) out[o.key] = byUid.get(o.uid);
  return out;
}

/**
 * The route table after the questions went from `prevRows` to `nextRows`.
 * Returns the same object when nothing needed to change.
 */
export function syncRoutes(prevRows, nextRows, table) {
  if (!table || !Array.isArray(table.routes) || !table.routes.length) return table;
  const questions = toApi(nextRows);
  const moves = keyMoves(prevRows, nextRows);
  let changed = false;
  const routes = table.routes.map((r) => {
    const when = {};
    for (const [k, allowed] of Object.entries(r.when || {})) {
      const move = moves[k];
      const key = move ? move.key : k;
      const q = questions[key];
      if (!q) { changed = true; continue; }
      if (isRange(q)) {
        // A score range: kept, clamped to the scale; an old level list or a
        // question that changed type starts as the whole scale.
        const top = topOf(q);
        const r = allowed && !Array.isArray(allowed) ? allowed : { min: 0, max: top };
        const clamped = { min: Math.min(r.min, top), max: Math.min(r.max, top) };
        if (key !== k || clamped.min !== r.min || clamped.max !== r.max || Array.isArray(allowed)) changed = true;
        when[key] = clamped;
        continue;
      }
      if (!Array.isArray(allowed)) { changed = true; continue; }
      const opt = move ? optionMoves(move.prev, move.next) : {};
      const all = outcomesOf(q);
      const next = (allowed || []).map((o) => opt[o] || o).filter((o, i, a) => all.includes(o) && a.indexOf(o) === i);
      if (key !== k || next.length !== (allowed || []).length || next.some((o, i) => o !== allowed[i])) changed = true;
      when[key] = next;
    }
    return { ...r, when };
  });
  return changed ? { ...table, routes } : table;
}

/**
 * What a decide run (or test) chose, in words. `answers` as the decision
 * API returns them; `questions` and `table` from the node's config.
 * → { port, name, because: [string] } or null when nothing matched.
 */
export function explainDecision(questions, table, answers) {
  const outcomes = Object.fromEntries(Object.entries(answers || {}).map(([k, a]) => [k, a && a.outcome]));
  const picked = pickRoute(questions || {}, table || { routes: [], otherwise: null }, outcomes);
  const because = Object.entries(answers || {}).map(([k, a]) => {
    const q = (questions || {})[k];
    const conf = a && typeof a.confidence === 'number' ? ` (${Math.round(a.confidence * 100)}%)` : '';
    return `${k} = ${q ? outcomeLabel(q, a.outcome) : a.outcome}${conf}`;
  });
  if (!picked) return { port: null, name: null, because };
  const port = picked.otherwise ? 'otherwise' : picked.route.id;
  const name = String(picked.route.name || '').trim() || (picked.otherwise ? 'Otherwise' : port);
  return { port, name, because };
}
